import { getCurrentInstance, onActivated, onBeforeUnmount, onDeactivated, onMounted } from 'vue';

// Keeps a page's data current across devices: what was added on the computer shows up on the
// iPad without reloading. The page quietly reloads when it comes back into view (another tab,
// the app returning to the front, the network coming back) and every half minute while it is
// showing. It holds off while someone types in a field or has a list or menu open, and while the
// page reports a save on its way, so nothing being entered is ever overwritten.
export const LIVE_REFRESH_INTERVAL_MS = 30000;
// Coming back usually fires several events at once (focus, visibility); they make one reload.
const MIN_GAP_MS = 4000;
// A reload that had to wait tries again shortly after the typing stops.
const RETRY_MS = 2500;

const FIELD_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
// Fields a reload cannot overwrite, such as search boxes, carry data-refresh-safe: focus in them
// (even a page's own autofocus) does not hold the reload back.

// An open list, menu, picker or dialog. Expanded sections (disclosures) are not popups and do
// not count, or a page with an open section would never reload.
const OPEN_POPUP_SELECTOR = '[aria-expanded="true"]:is([aria-haspopup]:not([aria-haspopup="false"]), [role="combobox"])';

/** Someone is typing somewhere, or a list or menu inside the page is open. */
export const isUserInteracting = (root) => {
	const active = typeof document !== 'undefined' ? document.activeElement : null;
	if (active && active !== document.body && active.matches?.(FIELD_SELECTOR) && !active.closest('[data-refresh-safe]')) return true;
	return Boolean(root?.querySelector?.(OPEN_POPUP_SELECTOR));
};

/**
 * @param {(context: { canApply: () => boolean }) => Promise<void>|void} refresh reloads the page's
 *   data without loaders; it calls canApply() before replacing anything, since the person may
 *   have started editing while the request was out.
 * @param {object} [options]
 * @param {() => boolean} [options.isBusy] the page's own reasons to wait (saves, creations, open dialogs)
 * @param {number} [options.interval]
 */
export function useLiveRefresh(refresh, { isBusy = () => false, interval = LIVE_REFRESH_INTERVAL_MS } = {}) {
	const instance = getCurrentInstance();
	const root = () => {
		const el = instance?.proxy?.$el;
		return el?.nodeType === 1 ? el : el?.parentElement ?? null;
	};

	let active = false;
	let running = false;
	let lastRun = 0;
	let intervalId = null;
	let retryId = null;

	const pageIsHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
	const blocked = () => isUserInteracting(root()) || isBusy();
	const canApply = () => active && !pageIsHidden() && !blocked();

	const run = async () => {
		clearTimeout(retryId);
		retryId = null;
		if (!active || running || pageIsHidden()) return;
		if (Date.now() - lastRun < MIN_GAP_MS) return;
		if (blocked()) {
			retryId = setTimeout(run, RETRY_MS);
			return;
		}
		running = true;
		lastRun = Date.now();
		try {
			await refresh({ canApply });
		} catch (error) {
			// A missed reload is simply tried again on the next occasion.
			console.warn('[live refresh]', error);
		} finally {
			running = false;
		}
		// Typing began, or the app went to the background, while the request was out, so its
		// result may have been left unused: try again as soon as that passes.
		if (active && !canApply()) {
			lastRun = 0;
			if (!pageIsHidden()) retryId = setTimeout(run, RETRY_MS);
		}
	};

	const onVisibility = () => {
		if (!pageIsHidden()) void run();
	};

	const onReturn = () => {
		void run();
	};

	const start = () => {
		if (active) return;
		active = true;
		intervalId = setInterval(run, interval);
		document.addEventListener('visibilitychange', onVisibility);
		window.addEventListener('focus', onReturn);
		window.addEventListener('online', onReturn);
		window.addEventListener('pageshow', onReturn);
	};

	const stop = () => {
		active = false;
		clearInterval(intervalId);
		clearTimeout(retryId);
		intervalId = null;
		retryId = null;
		document.removeEventListener('visibilitychange', onVisibility);
		window.removeEventListener('focus', onReturn);
		window.removeEventListener('online', onReturn);
		window.removeEventListener('pageshow', onReturn);
	};

	onMounted(() => {
		// The page loads its own data when it first appears.
		lastRun = Date.now();
		start();
	});

	// Coming back to a page kept in its tab reloads it straight away.
	onActivated(() => {
		start();
		void run();
	});

	onDeactivated(stop);
	onBeforeUnmount(stop);

	return { refreshNow: run };
}

// Written the same way whatever order the fields were set in; the page's own markers (__pending)
// are not part of the record.
const canonical = (value) => {
	if (Array.isArray(value)) return value.map(canonical);
	if (value && typeof value === 'object') {
		return Object.keys(value)
			.filter((key) => !key.startsWith('__') && value[key] !== undefined)
			.sort()
			.reduce((result, key) => {
				result[key] = canonical(value[key]);
				return result;
			}, {});
	}
	return value;
};

/** Whether two lists (or records) hold the same data, so an unchanged reload leaves the page alone. */
export const sameRecords = (a, b) => {
	if (a === b) return true;
	if (Array.isArray(a) && Array.isArray(b) && a.length !== b.length) return false;
	try {
		return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
	} catch {
		return false;
	}
};
